/**
 * Draft.js to Markdown Converter
 * Converts a Draft.js raw object to a markdown string
 */

// Types for Draft.js raw content
interface DraftEntityMap {
  [key: string]: DraftEntity;
}

interface DraftEntity {
  type: string;
  mutability: string;
  data: {
    [key: string]: any;
    url?: string;
    href?: string;
    language?: string;
  };
}

interface DraftInlineStyleRange {
  offset: number;
  length: number;
  style: string;
}

interface DraftEntityRange {
  offset: number;
  length: number;
  key: number;
}

interface DraftBlock {
  key?: string;
  text: string;
  type: string;
  depth: number;
  inlineStyleRanges: DraftInlineStyleRange[];
  entityRanges: DraftEntityRange[];
  data: {
    [key: string]: any;
    language?: string;
  };
}

interface RawDraftContentState {
  blocks: DraftBlock[];
  entityMap: DraftEntityMap;
}

// Interface for style and entity handlers
interface MarkdownStyleHandler {
  open: (block?: DraftBlock, number?: number) => string;
  close: (block?: DraftBlock) => string;
}

interface MarkdownEntityHandler {
  open: (entity: DraftEntity) => string;
  close: (entity: DraftEntity) => string;
}

interface StyleItems {
  [key: string]: MarkdownStyleHandler;
}

interface EntityItems {
  [key: string]: MarkdownEntityHandler;
}

interface DraftToMarkdownOptions {
  styleItems?: StyleItems;
  entityItems?: EntityItems;
  escapeMarkdownCharacters?: boolean;
  preserveNewlines?: boolean;
}

// Tag used in parsing
interface Tag {
  offset: number;
  length: number;
  style?: string;
  key?: number;
}

// For tracking markdown characters
interface MarkdownCharacter {
  character: string;
  index: number;
  markdownStringIndexStart: number;
  markdownStringIndexEnd: number;
}

// Constants
const TRAILING_WHITESPACE = /[ \u0020\t\n]*$/;
const MARKDOWN_STYLE_CHARACTERS = ['*', '_', '~', '`'];
const MARKDOWN_STYLE_CHARACTER_REGXP = /(\*|_|~|\\|`)/g;
const SINGLE_NEWLINE_AFTER_BLOCK = ['unordered-list-item', 'ordered-list-item'];

// Default style handlers
const DEFAULT_STYLE_ITEMS: StyleItems = {
  // BLOCK LEVEL
  'unordered-list-item': {
    open: () => '- ',
    close: () => ''
  },

  'ordered-list-item': {
    open: (_block?: DraftBlock, number: number = 1) => `${number}. `,
    close: () => ''
  },

  'blockquote': {
    open: () => '> ',
    close: () => ''
  },

  'header-one': {
    open: () => '# ',
    close: () => ''
  },

  'header-two': {
    open: () => '## ',
    close: () => ''
  },

  'header-three': {
    open: () => '### ',
    close: () => ''
  },

  'header-four': {
    open: () => '#### ',
    close: () => ''
  },

  'header-five': {
    open: () => '##### ',
    close: () => ''
  },

  'header-six': {
    open: () => '###### ',
    close: () => ''
  },

  'code-block': {
    open: (block?: DraftBlock) => '```' + (block?.data?.language || '') + '\n',
    close: () => '\n```'
  },

  // INLINE LEVEL
  'BOLD': {
    open: () => '**',
    close: () => '**'
  },

  'ITALIC': {
    open: () => '_',
    close: () => '_'
  },

  'STRIKETHROUGH': {
    open: () => '~~',
    close: () => '~~'
  },

  'CODE': {
    open: () => '`',
    close: () => '`'
  }
};

// Default entity handlers
const DEFAULT_ENTITY_ITEMS: EntityItems = {
  'LINK': {
    open: () => '[',
    close: (entity: DraftEntity) => `](${entity.data.url || entity.data.href})`
  }
};

/**
 * Check if a block is empty
 */
function isEmptyBlock(block: DraftBlock): boolean {
  return block.text.length === 0 &&
    block.entityRanges.length === 0 &&
    Object.keys(block.data || {}).length === 0;
}

/**
 * Utility function to reverse an array without mutating the original
 */
function reverse<T>(array: T[]): T[] {
  return array.concat().reverse();
}

/**
 * Comparator for sorting tags in reverse order of their closing position
 */
function compareTagsLastCloseFirst(a: Tag, b: Tag): number {
  return b.offset + b.length - (a.offset + a.length);
}

/**
 * Class to handle ordered list numbering
 */
class OrderedListTracker {
  private numbers: Record<number, number> = {};
  private previousDepth: number = 0;

  reset(): void {
    this.numbers = {};
  }

  getNumber(depth: number): number {
    this.numbers[depth] = this.numbers[depth] || 1;
    return this.numbers[depth]++;
  }

  updatePreviousDepth(depth: number): void {
    if (this.previousDepth > depth) {
      this.numbers[this.previousDepth] = 1;
    }
    this.previousDepth = depth;
  }
}

/**
 * Renders a Draft.js block as markdown
 */
function renderBlock(
  block: DraftBlock,
  index: number,
  rawDraftObject: RawDraftContentState,
  options: DraftToMarkdownOptions,
  orderedListTracker: OrderedListTracker
): string {
  const customStyleItems = options.styleItems || {};
  const customEntityItems = options.entityItems || {};
  const escapeMarkdownCharacters = options.hasOwnProperty('escapeMarkdownCharacters')
    ? options.escapeMarkdownCharacters
    : true;

  let type = block.type;
  let markdownString = '';
  let openTags: Tag[] = [];
  let markdownToAdd: Array<{ type: string; style?: Tag; value: string }> = [];
  let markdownStyleCharactersToEscape: MarkdownCharacter[] = [];

  // Handle empty blocks
  if (isEmptyBlock(block) && !options.preserveNewlines) {
    type = 'unstyled';
  }

  // Render block opening
  const styleHandler = customStyleItems[type] || DEFAULT_STYLE_ITEMS[type];
  if (styleHandler) {
    if (type === 'unordered-list-item' || type === 'ordered-list-item') {
      markdownString += ' '.repeat(block.depth * 4);
    }

    if (type === 'ordered-list-item') {
      const number = orderedListTracker.getNumber(block.depth);
      markdownString += styleHandler.open(block, number);
      orderedListTracker.updatePreviousDepth(block.depth);
    } else {
      orderedListTracker.reset();
      markdownString += styleHandler.open(block);
    }
  } else {
    orderedListTracker.reset();
  }

  /**
   * Opens a tag (inline style or entity)
   */
  function openTag(tag: Tag): void {
    openTags.push(tag);

    if (tag.style) {
      // Open inline tag
      const styleHandler = customStyleItems[tag.style] || DEFAULT_STYLE_ITEMS[tag.style];
      if (styleHandler) {
        markdownToAdd.push({
          type: 'style',
          style: tag,
          value: styleHandler.open()
        });
      }
    } else if (tag.key !== undefined) {
      // Open entity tag
      const entity = rawDraftObject.entityMap[tag.key.toString()];
      const entityHandler = customEntityItems[entity.type] || DEFAULT_ENTITY_ITEMS[entity.type];
      if (entityHandler) {
        markdownToAdd.push({
          type: 'entity',
          value: entityHandler.open(entity)
        });
      }
    }
  }

  /**
   * Closes a tag (inline style or entity)
   */
  function closeTag(tag: Tag): void {
    const popped = openTags.pop();
    if (!popped || tag !== popped) {
      throw new Error(
        'Invariant violation: Cannot close a tag before all inner tags have been closed'
      );
    }

    if (tag.style) {
      // Close inline tag
      const styleHandler = customStyleItems[tag.style] || DEFAULT_STYLE_ITEMS[tag.style];
      if (styleHandler) {
        // Handle trailing whitespace
        const trailingWhitespace = TRAILING_WHITESPACE.exec(markdownString);
        if (trailingWhitespace && trailingWhitespace[0]) {
          markdownString = markdownString.slice(
            0,
            markdownString.length - trailingWhitespace[0].length
          );
          markdownString += styleHandler.close();
          markdownString += trailingWhitespace[0];
        } else {
          markdownString += styleHandler.close();
        }
      }
    } else if (tag.key !== undefined) {
      // Close entity tag
      const entity = rawDraftObject.entityMap[tag.key.toString()];
      const entityHandler = customEntityItems[entity.type] || DEFAULT_ENTITY_ITEMS[entity.type];
      if (entityHandler) {
        markdownString += entityHandler.close(entity);
      }
    }
  }

  // Process each character in the block
  Array.from(block.text).forEach((character, characterIndex) => {
    // Close any tags that need closing
    reverse(openTags).forEach((tag) => {
      if (tag.offset + tag.length === characterIndex) {
        // Handle nested tags - close and reopen as needed
        const tagsToSplit = openTags.slice(openTags.indexOf(tag) + 1);
        reverse(tagsToSplit).forEach(closeTag);
        closeTag(tag);
        tagsToSplit.sort(compareTagsLastCloseFirst).forEach(openTag);
      }
    });

    // Open any tags that need opening
    const inlineTagsToOpen = block.inlineStyleRanges.filter(
      (tag) => tag.offset === characterIndex
    );
    const entityTagsToOpen = block.entityRanges.filter(
      (tag) => tag.offset === characterIndex
    );

    inlineTagsToOpen
      .concat(entityTagsToOpen)
      .sort(compareTagsLastCloseFirst)
      .forEach(openTag);

    // Apply any pending markdown styles before non-whitespace characters
    if (character !== ' ' && markdownToAdd.length) {
      markdownString += markdownToAdd.map((item) => item.value).join('');
      markdownToAdd = [];
    }

    // Handle markdown character escaping
    let processedCharacter = character;
    if (block.type !== 'code-block' && escapeMarkdownCharacters) {
      const insideInlineCodeStyle = openTags.find((tag) => tag.style === 'CODE');

      if (!insideInlineCodeStyle) {
        // Special handling for block quote and heading characters
        if (characterIndex === 0) {
          if (character === '#' && block.text[1] === ' ') {
            processedCharacter = '\\#';
          } else if (character === '>') {
            processedCharacter = '\\>';
          }
        }

        // Handle escaping of markdown style characters
        if (MARKDOWN_STYLE_CHARACTERS.includes(character)) {
          processedCharacter = handleMarkdownStyleCharacter(
            character,
            characterIndex,
            block.text,
            markdownString,
            markdownStyleCharactersToEscape
          );
        }
      }
    }

    // Add line breaks for block quotes
    if (character === '\n' && type === 'blockquote') {
      markdownString += '\n> ';
    } else {
      markdownString += processedCharacter;
    }
  });

  // Close any remaining open tags
  reverse(openTags).forEach(closeTag);

  // Close block level item
  if (styleHandler) {
    markdownString += styleHandler.close(block);
  }

  // Add appropriate newlines
  markdownString += determineNewlines(type, index, rawDraftObject, options);

  return markdownString;
}

/**
 * Handles markdown style character escaping
 */
function handleMarkdownStyleCharacter(
  character: string,
  characterIndex: number,
  text: string,
  markdownString: string,
  markdownStyleCharactersToEscape: MarkdownCharacter[]
): string {
  // Find if this is part of an already tracked opening style
  let openingStyle = markdownStyleCharactersToEscape.find(
    item => item.character === character
  );

  // Check if this is a new potential opening style
  if (!openingStyle && text[characterIndex - 1] === ' ' && text[characterIndex + 1] !== ' ') {
    markdownStyleCharactersToEscape.push({
      character,
      index: characterIndex,
      markdownStringIndexStart: markdownString.length,
      markdownStringIndexEnd: markdownString.length + 1
    });
    return character;
  }

  // Handle consecutive style characters
  else if (openingStyle && text[characterIndex - 1] === character && characterIndex === openingStyle.index + 1) {
    openingStyle.markdownStringIndexEnd += 1;
    return character;
  }

  // Check if this might be a closing style character
  else if (openingStyle) {
    let openingStyleLength = openingStyle.markdownStringIndexEnd - openingStyle.markdownStringIndexStart;
    let escapeCharacter = false;
    let popOpeningStyle = false;

    // Single character style (e.g., _word_)
    if (openingStyleLength === 1 && (text[characterIndex + 1] === ' ' || !text[characterIndex + 1])) {
      popOpeningStyle = true;
      escapeCharacter = true;
    }

    // Double character style handling (e.g., **word**)
    if (openingStyleLength === 2 && text[characterIndex + 1] === character) {
      escapeCharacter = true;
    }

    if (openingStyleLength === 2 && text[characterIndex - 1] === character &&
      (text[characterIndex + 1] === ' ' || !text[characterIndex + 1])) {
      popOpeningStyle = true;
      escapeCharacter = true;
    }

    // Apply escaping to the opening character(s) too
    if (popOpeningStyle) {
      markdownStyleCharactersToEscape.splice(
        markdownStyleCharactersToEscape.indexOf(openingStyle), 1
      );

      let replacementString = markdownString.slice(
        openingStyle.markdownStringIndexStart,
        openingStyle.markdownStringIndexEnd
      );

      replacementString = replacementString.replace(MARKDOWN_STYLE_CHARACTER_REGXP, '\\$1');

      markdownString = (
        markdownString.slice(0, openingStyle.markdownStringIndexStart) +
        replacementString +
        markdownString.slice(openingStyle.markdownStringIndexEnd)
      );
    }

    // Escape the current character if needed
    return escapeCharacter ? `\\${character}` : character;
  }

  return character;
}

/**
 * Determines how many newlines to add after a block
 */
function determineNewlines(
  type: string,
  index: number,
  rawDraftObject: RawDraftContentState,
  options: DraftToMarkdownOptions
): string {
  const nextBlock = rawDraftObject.blocks[index + 1];

  if (!nextBlock) {
    return '';
  }

  // For list items followed by another list item, use single newline
  if (SINGLE_NEWLINE_AFTER_BLOCK.includes(type) &&
    SINGLE_NEWLINE_AFTER_BLOCK.includes(nextBlock.type)) {
    return '\n';
  }

  // For list items followed by non-list items, use double newline
  else if (rawDraftObject.blocks[index].text) {
    if (SINGLE_NEWLINE_AFTER_BLOCK.includes(type) &&
      !SINGLE_NEWLINE_AFTER_BLOCK.includes(nextBlock.type)) {
      return '\n\n';
    } else if (!options.preserveNewlines) {
      // Double newlines if not preserving newlines
      return '\n\n';
    } else {
      return '\n';
    }
  }

  // Handle empty blocks
  else if (options.preserveNewlines) {
    return '\n';
  }

  return '';
}

/**
 * Convert a Draft.js raw content state to markdown
 */
export function draftToMarkdown(
  rawDraftObject: RawDraftContentState,
  options: DraftToMarkdownOptions = {}
): string {
  let markdownString = '';
  const orderedListTracker = new OrderedListTracker();

  rawDraftObject.blocks.forEach((block, index) => {
    markdownString += renderBlock(block, index, rawDraftObject, options, orderedListTracker);
  });

  return markdownString;
}

export default draftToMarkdown;
